import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserAcademyName, getUserDisplayName } from '../api/auth';
import { AcademyMember, membersAPI, normalizeMembersPayload } from '../api/members';
import { useAuth } from '../context/AuthContext';

type StudentSortKey = 'name' | 'schoolName' | 'memberClass' | 'memo';
type SortDirection = 'asc' | 'desc';

type StudentMemberRow = {
  key: string;
  tauserId: string;
  name: string;
  schoolName: string;
  memberClass: string;
  memo: string;
  phone: string;
  parentPhone: string;
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
  eventday: unknown;
};

type WeeklySchedule = {
  monday: string;
  tuesday: string;
  wednesday: string;
  thursday: string;
  friday: string;
  saturday: string;
  sunday: string;
};

type RemedialSlot = {
  date: string;
  time: string;
};

const DEFAULT_REMEDIAL_TIME = '00:00';

const formatDateToYmd = (date: Date): string => {
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultRemedialDate = (): string => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 14);
  return formatDateToYmd(yesterday);
};

const normalizeText = (value: unknown): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const sanitizePhone = (phone: string): string => {
  return phone.replace(/[^0-9+]/g, '');
};

const getMemberKey = (member: AcademyMember, index: number): string => {
  const keyParts = [member.id, member.email, member.phone, member.uniqueKey].filter(Boolean);
  return keyParts.length > 0 ? keyParts.join('-') : `member-${index}`;
};

const getTauserId = (member: AcademyMember): string => {
  const rawId = member.tauser_id ?? member.tauserId ?? member.id;

  if (typeof rawId === 'number') {
    return String(rawId);
  }

  return normalizeText(rawId);
};

const extractErrorMessage = (error: any): string => {
  return String(
    error?.response?.data?.message || error?.response?.data || error?.message || '알 수 없는 오류'
  );
};

const createEmptySchedule = (): WeeklySchedule => ({
  monday: '',
  tuesday: '',
  wednesday: '',
  thursday: '',
  friday: '',
  saturday: '',
  sunday: '',
});

const toSchedulePayloadValue = (value: string): string => {
  return value ? value : ' ';
};

const createEmptyRemedialSlot = (): RemedialSlot => ({
  date: getDefaultRemedialDate(),
  time: DEFAULT_REMEDIAL_TIME,
});

const parseEventdayToRemedialSlot = (value: unknown): RemedialSlot => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const millis = value < 1_000_000_000_000 ? value * 1000 : value;
    const date = new Date(millis);
    if (Number.isNaN(date.getTime())) {
      return createEmptyRemedialSlot();
    }

    const year = String(date.getFullYear());
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hour}:${minute}`,
    };
  }

  if (typeof value !== 'string') {
    return createEmptyRemedialSlot();
  }

  const raw = value.trim();
  if (!raw) {
    return createEmptyRemedialSlot();
  }

  const dateMatch = raw.match(/(\d{4}-\d{2}-\d{2})/);
  const timeMatch = raw.match(/(\d{2}:\d{2})/);
  if (dateMatch && timeMatch) {
    return {
      date: dateMatch[1],
      time: timeMatch[1],
    };
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return createEmptyRemedialSlot();
  }

  const year = String(parsed.getFullYear());
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hour = String(parsed.getHours()).padStart(2, '0');
  const minute = String(parsed.getMinutes()).padStart(2, '0');

  return {
    date: `${year}-${month}-${day}`,
    time: `${hour}:${minute}`,
  };
};

const buildEventdayTimestamp = (slot: RemedialSlot): number => {
  const fallbackDate = getDefaultRemedialDate();
  const date = slot.date || fallbackDate;
  const time = slot.time || DEFAULT_REMEDIAL_TIME;
  const combined = new Date(`${date}T${time}:00`);

  if (Number.isNaN(combined.getTime())) {
    return new Date(`${fallbackDate}T${DEFAULT_REMEDIAL_TIME}:00`).getTime();
  }

  return combined.getTime();
};

const getScheduleFromMember = (member: AcademyMember): WeeklySchedule => ({
  monday: normalizeText(member.monday),
  tuesday: normalizeText(member.tuesday),
  wednesday: normalizeText(member.wednesday),
  thursday: normalizeText(member.thursday),
  friday: normalizeText(member.friday),
  saturday: normalizeText(member.saturday),
  sunday: normalizeText(member.sunday),
});

const AdminAttendancePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<AcademyMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [membersMessage, setMembersMessage] = useState('');
  const [studentSortKey, setStudentSortKey] = useState<StudentSortKey>('name');
  const [studentSortDirection, setStudentSortDirection] = useState<SortDirection>('asc');
  const [studentSearchQuery1, setStudentSearchQuery1] = useState('');
  const [studentSearchQuery2, setStudentSearchQuery2] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<StudentMemberRow | null>(null);
  const [scheduleByStudentId, setScheduleByStudentId] = useState<Record<string, WeeklySchedule>>({});
  const [remedialByStudentId, setRemedialByStudentId] = useState<Record<string, RemedialSlot>>({});
  const [editMemberClass, setEditMemberClass] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [isSavingStudentInfo, setIsSavingStudentInfo] = useState(false);
  const [studentInfoMessage, setStudentInfoMessage] = useState('');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');

  const academyName = getUserAcademyName(user);
  const displayName = getUserDisplayName(user);

  const studentMembers = useMemo<StudentMemberRow[]>(() => {
    return members
      .map((member, index) => {
        const role = normalizeText(member.role).toLowerCase();
        if (role !== 'student') {
          return null;
        }

        return {
          key: getMemberKey(member, index),
          tauserId: getTauserId(member),
          name:
            normalizeText(member.name) ||
            normalizeText(member.username) ||
            normalizeText(member.email) ||
            `학생 ${index + 1}`,
          schoolName: normalizeText(member.schoolName) || '-',
          memberClass: normalizeText(member.class) || '-',
          memo: normalizeText(member.memo) || '-',
          phone: normalizeText(member.phone) || '-',
          parentPhone: normalizeText(member.parentPhone || member.parent_phone) || '-',
          ...getScheduleFromMember(member),
          eventday: member.eventday,
        };
      })
      .filter((member): member is StudentMemberRow => member !== null)
      .sort((left, right) => {
        const leftValue = left[studentSortKey];
        const rightValue = right[studentSortKey];
        const compared = leftValue.localeCompare(rightValue, 'ko');

        return studentSortDirection === 'asc' ? compared : compared * -1;
      });
  }, [members, studentSortDirection, studentSortKey]);

  const filteredStudentMembers = useMemo(() => {
    const q1 = studentSearchQuery1.trim().toLowerCase();
    const q2 = studentSearchQuery2.trim().toLowerCase();

    const matchesQuery = (member: StudentMemberRow, query: string): boolean => {
      if (!query) {
        return true;
      }

      return (
        member.name.toLowerCase().includes(query) ||
        member.schoolName.toLowerCase().includes(query) ||
        member.memberClass.toLowerCase().includes(query) ||
        member.memo.toLowerCase().includes(query)
      );
    };

    return studentMembers.filter(
      (member) => matchesQuery(member, q1) && matchesQuery(member, q2)
    );
  }, [studentMembers, studentSearchQuery1, studentSearchQuery2]);

  const currentSchedule = useMemo(() => {
    if (!selectedStudent) {
      return createEmptySchedule();
    }

    const key = selectedStudent.tauserId || selectedStudent.key;
    return scheduleByStudentId[key] || createEmptySchedule();
  }, [scheduleByStudentId, selectedStudent]);

  const currentRemedial = useMemo(() => {
    if (!selectedStudent) {
      return createEmptyRemedialSlot();
    }

    const key = selectedStudent.tauserId || selectedStudent.key;
    return remedialByStudentId[key] || createEmptyRemedialSlot();
  }, [remedialByStudentId, selectedStudent]);

  const selectedPhone = selectedStudent ? sanitizePhone(selectedStudent.phone) : '';
  const selectedParentPhone = selectedStudent ? sanitizePhone(selectedStudent.parentPhone) : '';
  const canCallStudent = selectedPhone.length > 0;
  const canCallParent = selectedParentPhone.length > 0;

  const loadMembers = useCallback(async () => {
    if (!academyName) {
      setMembers([]);
      setMembersMessage('관리자 계정에 학원명이 없어 회원 목록을 조회할 수 없습니다.');
      setIsLoadingMembers(false);
      return;
    }

    setIsLoadingMembers(true);

    try {
      setMembersMessage('');
      const response = await membersAPI.getAcademyMembers(academyName);
      setMembers(normalizeMembersPayload(response.data));
    } catch (error: any) {
      setMembersMessage(`회원 목록을 불러오지 못했습니다: ${extractErrorMessage(error)}`);
    } finally {
      setIsLoadingMembers(false);
    }
  }, [academyName]);

  useEffect(() => {
    loadMembers().catch(() => undefined);
  }, [loadMembers]);

  const handleStudentSort = (nextSortKey: StudentSortKey) => {
    if (studentSortKey === nextSortKey) {
      setStudentSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setStudentSortKey(nextSortKey);
    setStudentSortDirection('asc');
  };

  const getStudentSortIndicator = (columnKey: StudentSortKey): string => {
    if (studentSortKey !== columnKey) {
      return 'ㆍ';
    }

    return studentSortDirection === 'asc' ? '▲' : '▼';
  };

  const handleSelectStudent = (student: StudentMemberRow) => {
    setSelectedStudent(student);
    setEditMemberClass(student.memberClass === '-' ? '' : student.memberClass);
    setEditMemo(student.memo === '-' ? '' : student.memo);
    setStudentInfoMessage('');
    setScheduleMessage('');

    const key = student.tauserId || student.key;
    setScheduleByStudentId((current) => {
      if (current[key]) {
        return current;
      }

      return {
        ...current,
        [key]: {
          monday: student.monday,
          tuesday: student.tuesday,
          wednesday: student.wednesday,
          thursday: student.thursday,
          friday: student.friday,
          saturday: student.saturday,
          sunday: student.sunday,
        },
      };
    });

    setRemedialByStudentId((current) => {
      if (current[key]) {
        return current;
      }

      return {
        ...current,
        [key]: parseEventdayToRemedialSlot(student.eventday),
      };
    });
  };

  const handleSaveStudentInfo = async () => {
    if (!selectedStudent) {
      return;
    }

    if (!selectedStudent.tauserId) {
      setStudentInfoMessage('수정할 회원의 tauser_id를 찾을 수 없습니다.');
      return;
    }

    setIsSavingStudentInfo(true);
    setStudentInfoMessage('');

    try {
      await membersAPI.updateTauser(selectedStudent.tauserId, {
        class: editMemberClass,
        memo: editMemo,
      });

      const nextClass = normalizeText(editMemberClass) || '-';
      const nextMemo = normalizeText(editMemo) || '-';

      setSelectedStudent((current) =>
        current
          ? {
              ...current,
              memberClass: nextClass,
              memo: nextMemo,
            }
          : current
      );

      setStudentInfoMessage('반편성과 메모가 저장되었습니다.');
      await loadMembers();
    } catch (error: any) {
      setStudentInfoMessage(`저장 실패: ${extractErrorMessage(error)}`);
    } finally {
      setIsSavingStudentInfo(false);
    }
  };

  const handleScheduleChange = (day: keyof WeeklySchedule, value: string) => {
    if (!selectedStudent) {
      return;
    }

    const key = selectedStudent.tauserId || selectedStudent.key;
    setScheduleByStudentId((current) => ({
      ...current,
      [key]: {
        ...(current[key] || createEmptySchedule()),
        [day]: value,
      },
    }));
    setScheduleMessage('');
  };

  const handleClearScheduleDay = (day: keyof WeeklySchedule) => {
    handleScheduleChange(day, '');
    setScheduleMessage(`${day} 시간이 초기화되었습니다.`);
  };

  const handleRemedialChange = (field: keyof RemedialSlot, value: string) => {
    if (!selectedStudent) {
      return;
    }

    const key = selectedStudent.tauserId || selectedStudent.key;
    setRemedialByStudentId((current) => ({
      ...current,
      [key]: {
        ...(current[key] || createEmptyRemedialSlot()),
        [field]: value,
      },
    }));
    setScheduleMessage('');
  };

  const handleClearRemedial = () => {
    if (!selectedStudent) {
      return;
    }

    const key = selectedStudent.tauserId || selectedStudent.key;
    setRemedialByStudentId((current) => ({
      ...current,
      [key]: createEmptyRemedialSlot(),
    }));
    setScheduleMessage('보강 일정이 초기화되었습니다.');
  };

  const handleSaveSchedule = async () => {
    if (!selectedStudent) {
      return;
    }

    if (!selectedStudent.tauserId) {
      setScheduleMessage('저장할 회원의 tauser_id를 찾을 수 없습니다.');
      return;
    }

    const payload: WeeklySchedule & { eventday: number } = {
      monday: toSchedulePayloadValue(currentSchedule.monday),
      tuesday: toSchedulePayloadValue(currentSchedule.tuesday),
      wednesday: toSchedulePayloadValue(currentSchedule.wednesday),
      thursday: toSchedulePayloadValue(currentSchedule.thursday),
      friday: toSchedulePayloadValue(currentSchedule.friday),
      saturday: toSchedulePayloadValue(currentSchedule.saturday),
      sunday: toSchedulePayloadValue(currentSchedule.sunday),
      eventday: buildEventdayTimestamp(currentRemedial),
    };

    setIsSavingSchedule(true);
    setScheduleMessage('');

    try {
      await membersAPI.updateTauser(selectedStudent.tauserId, payload);

      setSelectedStudent((current) =>
        current
          ? {
              ...current,
              ...payload,
            }
          : current
      );

      setScheduleByStudentId((current) => {
        const key = selectedStudent.tauserId || selectedStudent.key;
        return {
          ...current,
          [key]: {
            monday: payload.monday,
            tuesday: payload.tuesday,
            wednesday: payload.wednesday,
            thursday: payload.thursday,
            friday: payload.friday,
            saturday: payload.saturday,
            sunday: payload.sunday,
          },
        };
      });

      setScheduleMessage('시간표가 저장되었습니다.');
      await loadMembers();
    } catch (error: any) {
      setScheduleMessage(`시간표 저장 실패: ${extractErrorMessage(error)}`);
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <main className="auth-page">
      <section className="auth-card admin-card">
        <p className="auth-eyebrow">LEARNING SCHEDULE</p>
        <h1>{displayName} 관리자님, 반갑습니다</h1>
        <p className="auth-description">
          학습일정관리 화면에서 학생별 요일 시간표와 보강 일정을 관리하세요.
        </p>

        <div className="admin-action-row">
          <button
            type="button"
            className="auth-primary-button admin-refresh-button"
            onClick={() => {
              loadMembers().catch(() => undefined);
            }}
            disabled={isLoadingMembers}
          >
            {isLoadingMembers ? '갱신 중...' : '명단 갱신'}
          </button>
          <button type="button" className="auth-secondary-button admin-nav-button" onClick={() => navigate('/admin/home')}>
            관리자 홈
          </button>
          <button type="button" className="auth-secondary-button admin-logout-button" onClick={handleLogout}>
            로그아웃
          </button>
        </div>

        <div className="admin-summary-grid">
          <div className="admin-summary-item">
            <span>학원명</span>
            <strong>{academyName || '-'}</strong>
          </div>
          <div className="admin-summary-item">
            <span>학생 수</span>
            <strong>{studentMembers.length}</strong>
          </div>
        </div>

        {membersMessage ? <p className="auth-inline-message">{membersMessage}</p> : null}

        {isLoadingMembers ? (
          <p className="auth-inline-message">회원 목록을 불러오는 중입니다...</p>
        ) : (
          <section className="admin-table-section admin-table-stack">
            <div className="admin-table-section-header">
              <h2>학생 테이블</h2>
              <span>
                {(studentSearchQuery1.trim() || studentSearchQuery2.trim())
                  ? `${filteredStudentMembers.length} / ${studentMembers.length}명`
                  : `${studentMembers.length}명`}
              </span>
            </div>

            <div className="admin-search-row">
              <div className="admin-search-bar">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="검색어 1 (이름·학교·반·메모)"
                  value={studentSearchQuery1}
                  onChange={(event) => setStudentSearchQuery1(event.target.value)}
                />
                {studentSearchQuery1 ? (
                  <button
                    type="button"
                    className="admin-search-clear"
                    onClick={() => setStudentSearchQuery1('')}
                    aria-label="검색어 1 지우기"
                  >
                    ✕
                  </button>
                ) : null}
              </div>

              <div className="admin-search-bar">
                <input
                  type="text"
                  className="admin-search-input"
                  placeholder="검색어 2 (이름·학교·반·메모)"
                  value={studentSearchQuery2}
                  onChange={(event) => setStudentSearchQuery2(event.target.value)}
                />
                {studentSearchQuery2 ? (
                  <button
                    type="button"
                    className="admin-search-clear"
                    onClick={() => setStudentSearchQuery2('')}
                    aria-label="검색어 2 지우기"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>

            <div className="admin-table-shell">
              {filteredStudentMembers.length > 0 ? (
                <table className="admin-member-table">
                  <thead>
                    <tr>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('name')}
                        >
                          학생명 <span>{getStudentSortIndicator('name')}</span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('schoolName')}
                        >
                          학교 <span>{getStudentSortIndicator('schoolName')}</span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('memberClass')}
                        >
                          반편성 <span>{getStudentSortIndicator('memberClass')}</span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="admin-sort-button"
                          onClick={() => handleStudentSort('memo')}
                        >
                          메모 <span>{getStudentSortIndicator('memo')}</span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudentMembers.map((student) => (
                      <tr
                        key={student.key}
                        className={
                          selectedStudent?.key === student.key
                            ? 'admin-clickable-row selected-row'
                            : 'admin-clickable-row'
                        }
                        onClick={() => handleSelectStudent(student)}
                      >
                        <td>{student.name}</td>
                        <td>{student.schoolName}</td>
                        <td>{student.memberClass}</td>
                        <td>{student.memo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="auth-inline-message">
                  {(studentSearchQuery1.trim() || studentSearchQuery2.trim())
                    ? '검색 결과가 없습니다.'
                    : '같은 학원명의 학생이 아직 없습니다.'}
                </p>
              )}
            </div>
          </section>
        )}

        {selectedStudent ? (
          <section className="admin-detail-card attendance-detail-card">
            <div className="admin-detail-header">
              <h2>학생 출결 정보</h2>
              <span>{selectedStudent.name}</span>
            </div>

            <div className="admin-contact-actions">
              <a
                className={canCallStudent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallStudent ? `tel:${selectedPhone}` : undefined}
                aria-disabled={!canCallStudent}
                onClick={(event) => {
                  if (!canCallStudent) {
                    event.preventDefault();
                  }
                }}
              >
                학생 전화걸기
              </a>
              <a
                className={canCallParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallParent ? `tel:${selectedParentPhone}` : undefined}
                aria-disabled={!canCallParent}
                onClick={(event) => {
                  if (!canCallParent) {
                    event.preventDefault();
                  }
                }}
              >
                학부모 전화걸기
              </a>
              <a
                className={canCallStudent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallStudent ? `sms:${selectedPhone}` : undefined}
                aria-disabled={!canCallStudent}
                onClick={(event) => {
                  if (!canCallStudent) {
                    event.preventDefault();
                  }
                }}
              >
                학생 문자하기
              </a>
              <a
                className={canCallParent ? 'auth-secondary-button admin-contact-button' : 'auth-secondary-button admin-contact-button disabled'}
                href={canCallParent ? `sms:${selectedParentPhone}` : undefined}
                aria-disabled={!canCallParent}
                onClick={(event) => {
                  if (!canCallParent) {
                    event.preventDefault();
                  }
                }}
              >
                학부모 문자하기
              </a>
            </div>

            <div className="attendance-compact-grid">
              <p><strong>학교</strong><span>{selectedStudent.schoolName}</span></p>
              <label className="auth-field attendance-edit-field">
                <span>반편성</span>
                <input
                  type="text"
                  value={editMemberClass}
                  onChange={(event) => setEditMemberClass(event.target.value)}
                  placeholder="반편성을 입력하세요"
                />
              </label>
              <label className="auth-field attendance-edit-field attendance-edit-field-wide">
                <span>메모</span>
                <input
                  type="text"
                  value={editMemo}
                  onChange={(event) => setEditMemo(event.target.value)}
                  placeholder="메모를 입력하세요"
                />
              </label>
            </div>

            {studentInfoMessage ? <p className="auth-inline-message">{studentInfoMessage}</p> : null}

            <div className="attendance-info-action-row">
              <button
                type="button"
                className="auth-primary-button attendance-save-button"
                onClick={handleSaveStudentInfo}
                disabled={isSavingStudentInfo}
              >
                {isSavingStudentInfo ? '저장 중...' : '반편성/메모 저장'}
              </button>
            </div>

            <div className="attendance-schedule-grid">
              <label className="auth-field">
                <span>월</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.monday}
                    onChange={(event) => handleScheduleChange('monday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('monday')}>
                    초기화
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>화</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.tuesday}
                    onChange={(event) => handleScheduleChange('tuesday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('tuesday')}>
                    초기화
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>수</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.wednesday}
                    onChange={(event) => handleScheduleChange('wednesday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('wednesday')}>
                    초기화
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>목</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.thursday}
                    onChange={(event) => handleScheduleChange('thursday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('thursday')}>
                    초기화
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>금</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.friday}
                    onChange={(event) => handleScheduleChange('friday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('friday')}>
                    초기화
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>토</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.saturday}
                    onChange={(event) => handleScheduleChange('saturday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('saturday')}>
                    초기화
                  </button>
                </div>
              </label>
              <label className="auth-field">
                <span>일</span>
                <div className="attendance-time-input-row">
                  <input
                    type="time"
                    value={currentSchedule.sunday}
                    onChange={(event) => handleScheduleChange('sunday', event.target.value)}
                  />
                  <button type="button" className="attendance-time-clear" onClick={() => handleClearScheduleDay('sunday')}>
                    초기화
                  </button>
                </div>
              </label>
            </div>

            <div className="attendance-remedial-grid">
              <label className="auth-field">
                <span>보강 날짜</span>
                <input
                  type="date"
                  value={currentRemedial.date}
                  onChange={(event) => handleRemedialChange('date', event.target.value)}
                />
              </label>
              <label className="auth-field">
                <span>보강 시간</span>
                <input
                  type="time"
                  value={currentRemedial.time}
                  onChange={(event) => handleRemedialChange('time', event.target.value)}
                />
              </label>
              <button
                type="button"
                className="attendance-time-clear attendance-remedial-clear"
                onClick={handleClearRemedial}
              >
                보강 초기화
              </button>
            </div>

            {scheduleMessage ? <p className="auth-inline-message">{scheduleMessage}</p> : null}

            <div className="attendance-info-action-row">
              <button
                type="button"
                className="auth-primary-button attendance-save-button"
                onClick={handleSaveSchedule}
                disabled={isSavingSchedule}
              >
                {isSavingSchedule ? '시간표 저장 중...' : '시간표 저장'}
              </button>
            </div>
          </section>
        ) : null}
      </section>
    </main>
  );
};

export default AdminAttendancePage;
